import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import HistoryIcon from "@mui/icons-material/History";
import MenuIcon from "@mui/icons-material/Menu";
import {
    AppBar,
    Badge,
    Box,
    Container,
    Drawer,
    IconButton,
    Stack,
    Toolbar,
    Typography,
} from "@mui/material";
import { BellRing } from "lucide-react";
import { bindTrigger, usePopupState } from "material-ui-popup-state/hooks";
import type { ReactNode } from "react";
import { useState } from "react";
import { Outlet } from "react-router-dom";
import ActivityDrawer from "@/components/Layout/ActivityDrawer.tsx";
import DrawerContent from "@/components/Layout/DrawerContent.tsx";
import NotificationMenu from "@/components/Layout/NotificationMenu.tsx";
import UserMenu from "@/components/Layout/UserMenu.tsx";
import { useEmployee } from "@/contexts/EmployeeProvider.tsx";
import { useFetchNotificationsForUser } from "@/queries/useFetchNotificationsForUser.ts";
import { formatEmployeeNameWithExtension } from "@/utils/formatEmployeeNameWithExtension.ts";

const drawerWidth = 240;
const activityDrawerWidth = 300;

const Layout = (): ReactNode => {
    const { employee } = useEmployee();
    const userMenuState = usePopupState({ variant: "popover", popupId: "user-menu" });
    const notificationsMenuState = usePopupState({
        variant: "popover",
        popupId: "notification-menu",
    });
    const [mobileOpen, setMobileOpen] = useState(false);
    const [activityOpen, setActivityOpen] = useState(false);
    const [isClosing, setIsClosing] = useState(false);

    const { data: notificationData = [] } = useFetchNotificationsForUser();
    const unreadNotificationCount = notificationData.filter((n) => n.hasUnreadNotification).length;

    const handleDrawerClose = () => {
        setIsClosing(true);
        setMobileOpen(false);
    };

    const handleDrawerTransitionEnd = () => {
        setIsClosing(false);
    };

    const handleDrawerToggle = () => {
        if (!isClosing) {
            setMobileOpen(!mobileOpen);
        }
    };

    const drawerContent = <DrawerContent />;
    const activityContent = <ActivityDrawer />;

    return (
        <Box
            sx={{
                display: "flex",
            }}
        >
            <AppBar
                position="fixed"
                sx={{
                    width: {
                        xs: `calc(100% - ${activityOpen ? activityDrawerWidth : 0}px)`,
                        md: `calc(100% - ${drawerWidth}px - ${
                            activityOpen ? activityDrawerWidth : 0
                        }px)`,
                    },
                    ml: { md: `${drawerWidth}px` },
                    mr: `${activityOpen ? activityDrawerWidth : 0}px`,
                    height: "64px",
                    backgroundColor: "#313131",
                }}
            >
                <Toolbar>
                    <Typography
                        variant="h6"
                        sx={{
                            display: { xs: "none", md: "block" },
                            color: "#fff",
                            width: "100%",
                        }}
                    >
                        Cambre & Associates
                    </Typography>
                    <IconButton
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{ mr: 2, display: { md: "none" }, color: "#fff" }}
                    >
                        <MenuIcon />
                    </IconButton>
                    <Typography
                        variant="h6"
                        component="div"
                        sx={{ display: { md: "none" }, color: "#fff" }}
                    >
                        Cambre
                    </Typography>
                    <Stack
                        sx={{
                            flexDirection: "row",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            width: "100%",
                        }}
                    >
                        <IconButton
                            edge="end"
                            {...bindTrigger(notificationsMenuState)}
                            sx={{
                                ml: "8px",
                                borderRadius: "20px",
                                "&:hover": {
                                    borderRadius: "20px",
                                },
                                color: "#fff",
                            }}
                        >
                            <Badge badgeContent={unreadNotificationCount} color="error">
                                <BellRing size={20} />
                            </Badge>
                        </IconButton>
                        <IconButton
                            edge="end"
                            {...bindTrigger(userMenuState)}
                            sx={{
                                ml: "8px",
                                borderRadius: "20px",
                                "&:hover": {
                                    borderRadius: "20px",
                                },
                                color: "#fff",
                            }}
                        >
                            <AccountCircleIcon />
                            <Typography sx={{ mx: 1 }}>
                                {formatEmployeeNameWithExtension(
                                    employee?.fullName,
                                    employee?.extNumber,
                                )}
                            </Typography>
                        </IconButton>
                        <IconButton
                            sx={{
                                ml: "0",
                                color: "#fff",
                            }}
                            onClick={() => setActivityOpen(!activityOpen)}
                        >
                            <HistoryIcon />
                        </IconButton>
                    </Stack>
                    <NotificationMenu state={notificationsMenuState} />
                    <UserMenu state={userMenuState} />
                </Toolbar>
            </AppBar>
            <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onTransitionEnd={handleDrawerTransitionEnd}
                    onClose={handleDrawerClose}
                    ModalProps={{
                        keepMounted: true,
                    }}
                    sx={{
                        display: { xs: "block", md: "none" },
                        "& .MuiDrawer-paper": {
                            boxSizing: "border-box",
                            width: drawerWidth,
                            backgroundColor: "#313131",
                            color: "#fff",
                        },
                    }}
                >
                    {drawerContent}
                </Drawer>
                <Drawer
                    variant="permanent"
                    sx={{
                        display: { xs: "none", md: "block" },
                        "& .MuiDrawer-paper": {
                            boxSizing: "border-box",
                            width: drawerWidth,
                            backgroundColor: "#313131",
                            color: "#fff",
                        },
                    }}
                    open
                >
                    {drawerContent}
                </Drawer>
            </Box>

            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    py: 3,
                    width: {
                        xs: "100%",
                        md: `calc(100% - ${
                            drawerWidth + (activityOpen ? activityDrawerWidth : 0)
                        }px)`,
                    },
                    height: "calc(100vh - 64px)",
                }}
            >
                <Toolbar />
                <Container
                    maxWidth={false}
                    sx={{
                        flexGrow: 1,
                        height: "100%",
                    }}
                >
                    <Outlet />
                </Container>
            </Box>

            {activityOpen && (
                <Box
                    component="nav"
                    sx={{ width: { md: activityDrawerWidth }, flexShrink: { md: 0 } }}
                >
                    <Drawer
                        variant={"permanent"}
                        open={activityOpen}
                        anchor={"right"}
                        onTransitionEnd={handleDrawerTransitionEnd}
                        onClose={handleDrawerClose}
                        ModalProps={{
                            keepMounted: true,
                        }}
                        sx={{
                            "& .MuiDrawer-paper": {
                                boxSizing: "border-box",
                                width: activityDrawerWidth,
                            },
                        }}
                    >
                        {activityContent}
                    </Drawer>
                </Box>
            )}
        </Box>
    );
};

export default Layout;
